/**
 * Pure client-side view model for the tabular grid: sort + group lens over a
 * flat rows array. Never reorders `rows` in place — every emitted row node
 * carries `sourceIndex` pointing back into the original array.
 */

export type TabularSort = { field: string; dir: 'asc' | 'desc' } | null;

export type TabularViewNode =
  | { kind: 'group'; key: string; level: number; label: string; count: number; collapsed: boolean }
  | { kind: 'row'; sourceIndex: number; row: Record<string, unknown> };

export interface BuildTabularViewArgs {
  rows: Record<string, unknown>[];
  sort: TabularSort;
  groupBy: string[];
  /** Keys currently expanded. */
  expandedGroups: string[];
  /** When true (default), groups with no entry in expandedGroups start expanded. */
  expandAllDefault?: boolean;
  /** Case-insensitive substring filter. Empty / omitted = no filter. */
  search?: string;
  /** Column names to scan. Empty / omitted = every own key on the row. */
  searchFields?: string[];
}

export function rowMatchesSearch(
  row: Record<string, unknown>,
  query: string,
  fields?: string[],
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const keys = fields && fields.length > 0 ? fields : Object.keys(row);
  return keys.some((f) => String(row[f] ?? '').toLowerCase().includes(q));
}

const GROUP_KEY_SEPARATOR = '\u001f';

interface IndexedRow {
  row: Record<string, unknown>;
  sourceIndex: number;
}

function displayValue(value: unknown): string {
  return String(value ?? '(empty)');
}

function compareValues(a: unknown, b: unknown): number {
  const aNil = a === null || a === undefined;
  const bNil = b === null || b === undefined;
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b));
}

function stableSortIndexed(indexed: IndexedRow[], sort: { field: string; dir: 'asc' | 'desc' }): IndexedRow[] {
  const sign = sort.dir === 'desc' ? -1 : 1;
  return indexed
    .slice()
    .sort((a, b) => {
      const cmp = compareValues(a.row[sort.field], b.row[sort.field]) * sign;
      return cmp !== 0 ? cmp : a.sourceIndex - b.sourceIndex;
    });
}

function groupLevel(
  pairs: IndexedRow[],
  groupBy: string[],
  level: number,
  parentKeyParts: string[],
  isExpanded: (key: string) => boolean,
): TabularViewNode[] {
  const field = groupBy[level];
  const order: string[] = [];
  const buckets = new Map<string, IndexedRow[]>();

  for (const pair of pairs) {
    const label = displayValue(pair.row[field]);
    let bucket = buckets.get(label);
    if (!bucket) {
      bucket = [];
      buckets.set(label, bucket);
      order.push(label);
    }
    bucket.push(pair);
  }

  const nodes: TabularViewNode[] = [];
  for (const label of order) {
    const bucket = buckets.get(label) as IndexedRow[];
    const keyParts = [...parentKeyParts, label];
    const key = keyParts.join(GROUP_KEY_SEPARATOR);
    const collapsed = !isExpanded(key);

    nodes.push({ kind: 'group', key, level, label, count: bucket.length, collapsed });

    if (collapsed) continue;

    if (level + 1 < groupBy.length) {
      nodes.push(...groupLevel(bucket, groupBy, level + 1, keyParts, isExpanded));
    } else {
      for (const pair of bucket) {
        nodes.push({ kind: 'row', sourceIndex: pair.sourceIndex, row: pair.row });
      }
    }
  }

  return nodes;
}

export function buildTabularView(args: BuildTabularViewArgs): TabularViewNode[] {
  const { rows, sort, groupBy, expandedGroups, expandAllDefault = true, search = '', searchFields } = args;

  const indexed: IndexedRow[] = rows
    .map((row, sourceIndex) => ({ row, sourceIndex }))
    .filter((p) => rowMatchesSearch(p.row, search, searchFields));
  const sorted = sort ? stableSortIndexed(indexed, sort) : indexed;

  if (groupBy.length === 0) {
    return sorted.map(({ row, sourceIndex }) => ({ kind: 'row', sourceIndex, row }));
  }

  // Empty expandedGroups falls back to expandAllDefault. A non-empty list is the
  // sole source of truth (whitelist of expanded keys) — this is what makes
  // collapsing possible even when expandAllDefault is true.
  const isExpanded = (key: string): boolean =>
    expandedGroups.length === 0 ? expandAllDefault : expandedGroups.includes(key);

  return groupLevel(sorted, groupBy, 0, [], isExpanded);
}

export function cycleSort(current: TabularSort, field: string): TabularSort {
  if (!current || current.field !== field) return { field, dir: 'asc' };
  if (current.dir === 'asc') return { field, dir: 'desc' };
  return null;
}
